import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Typography,
    Grid,
    Button,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
} from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { IdBill, IdLeaseAgreement, BillStatus, ITenantInfo } from 'realhaus-sdk';
import { RootStoreContext } from '../../../global/storeContext';
import { WalletSummaryText } from '../../wallet/walletWidget';
import { SendOrRequestPaymentModal } from './request/payment/send/sendOrRequestModal';
import { useIsMobile } from '../../uiComponents/UIComponents';
import { LeaseBillSnapshot } from './billSnapshot';
import { PaymentCheckout } from '../../wallet/billPaymentCheckout';
import { Stack } from '@mui/material';
import { BillsReportComponent } from './billsReport';

export const LeaseBillsComponent: React.FC<{
    leaseAgreement: IdLeaseAgreement;
    tenant: ITenantInfo | undefined;
}> = observer(({ leaseAgreement, tenant }) => {
    const billStatuses = ['In Progress', 'Overdue', 'Pending', 'Paid', 'Canceled'];
    const { leaseStore, uiStore, walletStore } = useContext(RootStoreContext);
    const [statusFilter, setStatusFilter] = React.useState<string[]>(billStatuses);
    const [bills, setBills] = useState<IdBill[]>([]);
    const [inProgressBillIds, setInProgressBillIds] = useState<Set<string>>(new Set());
    const [openSendRequestPaymentModal, setOpenSendRequestPaymentModal] = React.useState(false);
    const [openPaymentCheckoutModal, setOpenPaymentCheckoutModal] = useState(false);
    const [openPaymentReportModal, setOpenPaymentReportModal] = React.useState(false);
    const [selectedBillId, setSelectedBillId] = useState<string | undefined>(undefined);

    const handleCloseSendRequestPaymentModal = () => {
        setOpenSendRequestPaymentModal(false);
    };

    const handleOpenSendRequestPaymentModal = () => {
        setOpenSendRequestPaymentModal(true);
    };

    const handleOpenPaymentReportModal = () => {
        setOpenPaymentReportModal(true);
    };

    const handleClosePaymentReportModal = () => {
        setOpenPaymentReportModal(false);
    };

    const isLandlordMode = uiStore.isLandlordMode();

    useEffect(() => {
        // fetch lease bills;
        fetchBills();
    }, []);

    const fetchBills = async () => {
        const leaseBills = await leaseStore.getLeaseBills(leaseAgreement.id);
        setInProgressBillIds(await walletStore.getInProgressBillIds(leaseBills));
        setBills(leaseBills);
    };

    const filteredBills = useMemo(() => {
        return bills.filter((b) => {
            let billStatus = b.status.toString();
            if (billStatus === BillStatus.PENDING) {
                if (inProgressBillIds.has(b.id)) {
                    billStatus = 'In Progress';
                } else if (b.dueDate < Date.now()) {
                    billStatus = 'Overdue';
                }
            }

            return statusFilter.find((s) => s.toLowerCase() === billStatus.toLowerCase());
        });
    }, [bills, inProgressBillIds, statusFilter]);

    const handleStatusFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const filteredValues = event.target.value as string[];
        setStatusFilter(filteredValues.length === 0 ? billStatuses : filteredValues);
    };

    const handlePaymentRequested = async () => {
        await fetchBills();
        console.log('Payment Requested');
    };

    const closeBillPaymentCheckout = async () => {
        setSelectedBillId(undefined);
        setOpenPaymentCheckoutModal(false);
        // A payment may have started (e.g. Interac), so refresh which bills are In Progress.
        await fetchBills();
    };

    const showPaymentCheckout = (billId: string) => {
        setSelectedBillId(billId);
        setOpenPaymentCheckoutModal(true);
    };

    const isMobile = useIsMobile();

    return (
        <>
            <Grid container spacing={2} direction='column'>
                <Grid
                    item
                    container
                    justifyContent='space-between'
                    spacing={1}
                    alignContent='center'
                >
                    <Grid item xs={12} md={8}>
                        <Stack direction='row' spacing={1}>
                            {isLandlordMode && (
                                <>
                                    <Button
                                        variant='outlined'
                                        color='primary'
                                        onClick={handleOpenSendRequestPaymentModal}
                                    >
                                        send/Request payment
                                    </Button>
                                </>
                            )}
                            <Button
                                variant='outlined'
                                color='primary'
                                onClick={handleOpenPaymentReportModal}
                            >
                                View Payment Report
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid
                        item
                        container
                        justifyContent={isMobile ? 'flex-start' : 'flex-end'}
                        xs={12}
                        md={4}
                    >
                        <Grid item>
                            <WalletSummaryText />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>Filter:</Grid>
                <Grid item>
                    <FormControl
                        variant='outlined'
                        size='small'
                        style={{ minWidth: '250px', maxWidth: '300px' }}
                    >
                        <Select
                            id='lease-status-filter'
                            variant='outlined'
                            multiple
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                            renderValue={(selected) => (selected as string[]).join(', ')}
                        >
                            {billStatuses.map((status) => (
                                <MenuItem key={status} value={status}>
                                    <Checkbox checked={!!statusFilter.find((f) => f === status)} />
                                    <ListItemText primary={status} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item>
                    {/* filtered Bills */}
                    {filteredBills?.length === 0 ? (
                        <Typography variant='subtitle1'>
                            {!!bills && bills?.length > 0
                                ? 'Could not find a lease bill that matches the filter'
                                : 'There are no bills available'}
                        </Typography>
                    ) : (
                        filteredBills?.map((bill) => (
                            <LeaseBillSnapshot
                                key={bill.id}
                                initBill={bill}
                                onPayBillClicked={() => showPaymentCheckout(bill.id)}
                            />
                        ))
                    )}
                </Grid>
            </Grid>
            <SendOrRequestPaymentModal
                open={openSendRequestPaymentModal}
                handleCloseModal={handleCloseSendRequestPaymentModal}
                tenantName={tenant?.firstname}
                leaseAgreement={leaseAgreement}
                onPaymentRequested={handlePaymentRequested}
                onSendMoneyBillCreated={async (billId) => {
                    await fetchBills();
                    showPaymentCheckout(billId);
                }}
            />

            <BillsReportComponent
                open={openPaymentReportModal}
                closeView={handleClosePaymentReportModal}
                leaseAgreement={leaseAgreement}
            ></BillsReportComponent>

            {!!selectedBillId && (
                <PaymentCheckout
                    type='BILL'
                    billId={selectedBillId}
                    leaseId={leaseAgreement.id}
                    open={openPaymentCheckoutModal}
                    handleClose={closeBillPaymentCheckout}
                    handleComplete={() => {
                        closeBillPaymentCheckout();
                    }}
                    handleFailure={() => {
                        console.log('Bill Payment Failed');
                    }}
                />
            )}
        </>
    );
});
